import { ReactElement, ReactNode } from 'react';
import { Tag } from 'antd';
import type { TFunction } from 'i18next';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, HourglassOutlined } from '@ant-design/icons';

import { ComplianceStatus, UserStatus } from '@pmp/api';

import { ReadonlyText } from './ReadonlyText';

export type StatusTagStyle = {
  label: ReactNode;
  color: string;
  icon?: ReactElement;
};

/** Keyed by the status value as a string, so a switch's `true` and `false` are keys too. */
export type StatusTagStyles = Record<string, StatusTagStyle>;

type ReadonlyStatusEntity = {
  value: unknown;
  [key: string]: unknown;
};

export const StatusTag = ({ status }: { status: StatusTagStyle }): ReactElement => (
  <Tag color={status.color} icon={status.icon}>
    {status.label}
  </Tag>
);

export const renderReadonlyStatusTag =
  (statuses: StatusTagStyles) =>
  (_dom: ReactNode | null, { value }: ReadonlyStatusEntity): ReactNode => {
    const status = value === null || value === undefined ? undefined : statuses[String(value)];
    return status ? <StatusTag status={status} /> : <ReadonlyText value={undefined} />;
  };

export const getActiveStatusTags = (t: TFunction): StatusTagStyles => ({
  true: { label: t('firms.labels.active'), color: 'success' },
  false: { label: t('firms.labels.inactive'), color: 'error' },
});

// The edit form holds an intermediary's status as a switch, a stored record holds the enum.
export const getUserStatusTags = (t: TFunction): StatusTagStyles => {
  const activeStatusTags = getActiveStatusTags(t);

  return {
    ...activeStatusTags,
    [UserStatus.ACTIVE]: activeStatusTags.true,
    [UserStatus.INACTIVE]: activeStatusTags.false,
    [UserStatus.PENDING]: { label: t('firms.labels.pending'), color: 'warning' },
  };
};

export const getComplianceStatusTags = (t: TFunction): StatusTagStyles => ({
  [ComplianceStatus.PENDING]: { label: t('firms.labels.pending'), color: 'warning', icon: <HourglassOutlined /> },
  [ComplianceStatus.FAILED]: { label: t('firms.labels.failed'), color: 'error', icon: <CloseCircleOutlined /> },
  [ComplianceStatus.PASSED]: { label: t('firms.labels.passed'), color: 'success', icon: <CheckCircleOutlined /> },
  [ComplianceStatus.RE_REVIEW_REQUIRED]: { label: t('firms.labels.reReviewRequired'), color: 'default', icon: <EyeOutlined /> },
});
